import { useEffect, useRef, useCallback } from 'react';

const FOCUSABLE_ELEMENTS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const useKeyboardNavigation = (refs, initialFocusRef) => {
  const handleTabNavigation = useCallback((e) => {
    if (e.key === 'Tab' && refs && Array.isArray(refs)) {
      // Handle both DOM elements directly and ref objects
      const focusableElements = refs
        .filter(ref => ref && (ref.current || ref.nodeType))
        .map(ref => ref.current || ref);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [refs]);

  useEffect(() => {
    document.addEventListener('keydown', handleTabNavigation);
    if (initialFocusRef?.current) {
      initialFocusRef.current.focus();
    }
    return () => document.removeEventListener('keydown', handleTabNavigation);
  }, [handleTabNavigation, initialFocusRef]);
};

export const useAriaAnnouncer = () => {
  const announceMessage = useCallback((message, ariaLive = 'polite') => {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', ariaLive);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.setAttribute('class', 'sr-only');
    document.body.appendChild(announcer);

    // Use setTimeout to ensure the message is announced
    setTimeout(() => {
      announcer.textContent = message;
      setTimeout(() => {
        document.body.removeChild(announcer);
      }, 1000);
    }, 100);
  }, []);

  return announceMessage;
};

export const useFocusTrap = (isActive) => {
  const elementRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    const element = elementRef.current;
    if (!element) return;

    const focusableElements = element.querySelectorAll(FOCUSABLE_ELEMENTS);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Store last focused element to restore focus when trap is deactivated
    const lastFocusedElement = document.activeElement;

    // Focus first focusable element when trap is activated
    firstFocusable?.focus();

    const handleKeydown = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    element.addEventListener('keydown', handleKeydown);

    return () => {
      element.removeEventListener('keydown', handleKeydown);
      lastFocusedElement?.focus();
    };
  }, [isActive]);

  return elementRef;
};

export const useEscapeKey = (callback) => {
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        callback();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [callback]);
};

export const useArrowKeyNavigation = (itemCount, onSelect, options = {}) => {
  const {
    isHorizontal = false,
    isCircular = true,
    initialIndex = 0
  } = options;

  const currentIndex = useRef(initialIndex);

  useEffect(() => {
    const handleKeydown = (e) => {
      const keys = isHorizontal 
        ? ['ArrowLeft', 'ArrowRight'] 
        : ['ArrowUp', 'ArrowDown'];

      if (!keys.includes(e.key)) return;

      e.preventDefault();
      
      const increment = e.key === keys[1] ? 1 : -1;
      let newIndex = currentIndex.current + increment;

      if (isCircular) {
        newIndex = (newIndex + itemCount) % itemCount;
      } else {
        newIndex = Math.max(0, Math.min(newIndex, itemCount - 1));
      }

      currentIndex.current = newIndex;
      onSelect(newIndex);
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [itemCount, onSelect, isHorizontal, isCircular]);

  return currentIndex.current;
};

export const useClickOutside = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, callback]);
};

export const announceToScreenReader = (message) => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.setAttribute('class', 'sr-only');
  document.body.appendChild(announcement);

  // Delay slightly to ensure screen readers pick up the change
  setTimeout(() => {
    announcement.textContent = message;
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }, 100);
};

export const getFocusableElements = (element) => {
  return Array.from(element.querySelectorAll(FOCUSABLE_ELEMENTS))
    .filter(el => !!(
      el.offsetWidth ||
      el.offsetHeight ||
      el.getClientRects().length
    ));
};